import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'privacy' | 'terms' | null;
}
export function LegalModal({
  isOpen,
  onClose,
  type
}: LegalModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);
  const content = type === 'privacy' ? privacyContent : termsContent;
  const title = type === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones';
  return <AnimatePresence data-id="element-157">
      {isOpen && type && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" onClick={onClose} data-id="element-158">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-mystic-black/80 backdrop-blur-sm" data-id="element-159" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} onClick={e => e.stopPropagation()} className="relative w-full max-w-3xl max-h-[85vh] glass-panel rounded-2xl overflow-hidden flex flex-col" data-id="element-160">
            {/* Header */}
            <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-mystic-gold/20 bg-mystic-black/40" data-id="element-161">
              <h2 className="text-xl sm:text-2xl font-serif font-bold text-gradient-gold" data-id="element-162">
                {title}
              </h2>
              <button onClick={onClose} aria-label="Cerrar" className="w-10 h-10 rounded-full flex items-center justify-center text-mystic-white/70 hover:text-mystic-gold hover:bg-mystic-gold/10 transition-colors" data-id="element-163">
                <X className="w-5 h-5" data-id="element-164" />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto px-6 sm:px-8 py-6 space-y-6 text-mystic-white/80 font-light leading-relaxed" data-id="element-165">
              {content.map((section, index) => <div key={index} data-id="element-166">
                  {section.heading && <h3 className="font-serif text-lg text-mystic-gold mb-2 font-semibold" data-id="element-167">
                      {section.heading}
                    </h3>}
                  <p className="text-sm sm:text-base whitespace-pre-line" data-id="element-168">
                    {section.body}
                  </p>
                </div>)}
              <p className="text-xs text-mystic-white/40 italic pt-4 border-t border-mystic-gold/10" data-id="element-169">
                Última actualización:{' '}
                {new Date().toLocaleDateString('es-CL', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
              </p>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
const privacyContent = [{
  heading: '1. Información general',
  body: 'En Maestro Navy respetamos tu privacidad y nos comprometemos a proteger los datos personales que compartas con nosotros. Esta política describe cómo recopilamos, usamos y protegemos tu información cuando utilizas nuestros servicios de orientación espiritual y bienestar.'
}, {
  heading: '2. Información que recopilamos',
  body: 'Podemos recopilar la siguiente información cuando te comunicas con nosotros vía WhatsApp u otros canales:\n\n• Nombre y datos de contacto que decidas compartir.\n• Información sobre tu situación personal compartida durante la consulta.\n• Fotografías enviadas voluntariamente para servicios de fotomancia.\n• Historial de comunicaciones para dar seguimiento adecuado.'
}, {
  heading: '3. Uso de la información',
  body: 'Tus datos se utilizan exclusivamente para:\n\n• Brindarte orientación personalizada.\n• Coordinar consultas y dar seguimiento al servicio.\n• Mejorar la calidad de la atención.\n\nNo vendemos, compartimos ni comercializamos tu información con terceros bajo ninguna circunstancia.'
}, {
  heading: '4. Confidencialidad',
  body: 'Toda la información compartida durante las consultas se trata con absoluta confidencialidad. Los detalles personales, fotografías y conversaciones no serán divulgados ni utilizados para ningún propósito ajeno al servicio solicitado.'
}, {
  heading: '5. Almacenamiento y seguridad',
  body: 'Mantenemos medidas razonables para proteger tu información frente a accesos no autorizados. Las conversaciones por WhatsApp se almacenan localmente y se eliminan periódicamente cuando ya no son necesarias.'
}, {
  heading: '6. Tus derechos',
  body: 'En cualquier momento puedes solicitar:\n\n• Acceso a la información que tenemos sobre ti.\n• Rectificación de datos incorrectos.\n• Eliminación total de tus datos de nuestros registros.\n\nPara ejercer estos derechos, contáctanos directamente por WhatsApp.'
}, {
  heading: '7. Cookies y sitio web',
  body: 'Este sitio web no utiliza cookies de seguimiento ni recopila información personal de quienes lo visitan. Solo se registran datos cuando contactas voluntariamente por WhatsApp.'
}, {
  heading: '8. Cambios en esta política',
  body: 'Podemos actualizar esta política de privacidad ocasionalmente. Los cambios se publicarán en esta misma página con la fecha de actualización correspondiente.'
}];
const termsContent = [{
  heading: '1. Aceptación de los términos',
  body: 'Al contactar y contratar los servicios de Maestro Navy, aceptas estos términos y condiciones en su totalidad. Si no estás de acuerdo con alguno de ellos, te pedimos abstenerte de solicitar el servicio.'
}, {
  heading: '2. Naturaleza de los servicios',
  body: 'Los servicios ofrecidos —videncia, fotomancia, limpieza energética de hogares y negocios— corresponden a orientación espiritual y prácticas de bienestar personal. No sustituyen ni reemplazan diagnósticos médicos, tratamientos psicológicos ni asesoría legal, financiera o profesional de ningún tipo.'
}, {
  heading: '3. Mayoría de edad',
  body: 'Para acceder a los servicios debes ser mayor de 18 años. Al solicitar una consulta declaras tener la edad legal requerida.'
}, {
  heading: '4. Modalidad de atención',
  body: 'Las consultas pueden realizarse de forma presencial en Antofagasta, Chile, o de manera remota según el servicio solicitado. Los horarios de atención y disponibilidad serán coordinados previamente por WhatsApp.'
}, {
  heading: '5. Pagos y tarifas',
  body: 'El valor de cada servicio será informado al momento de coordinar la consulta. Los pagos se realizan según las modalidades acordadas. No se procesan pagos directamente desde este sitio web.'
}, {
  heading: '6. Política de cancelación',
  body: 'Las cancelaciones o reprogramaciones deben comunicarse con al menos 24 horas de anticipación. Cancelaciones con menos tiempo podrían no ser reembolsables, dependiendo del caso.'
}, {
  heading: '7. Resultados y expectativas',
  body: 'Los resultados de las consultas dependen de múltiples factores personales y energéticos. Maestro Navy se compromete a brindar orientación profesional y respetuosa, pero no garantiza resultados específicos. Cada persona es responsable de las decisiones que tome a partir de la orientación recibida.'
}, {
  heading: '8. Conducta durante las consultas',
  body: 'Las consultas se realizan en un marco de respeto mutuo. Maestro Navy se reserva el derecho de finalizar una consulta ante conductas inapropiadas, ofensivas o que vulneren la dignidad de las personas involucradas.'
}, {
  heading: '9. Propiedad intelectual',
  body: 'Todos los contenidos de este sitio web —textos, imágenes, diseño y marca— son propiedad de Maestro Navy y están protegidos por las leyes de propiedad intelectual. Queda prohibida su reproducción total o parcial sin autorización expresa.'
}, {
  heading: '10. Limitación de responsabilidad',
  body: 'Maestro Navy no se hace responsable por decisiones tomadas por los consultantes a partir de la orientación recibida, ni por consecuencias derivadas de las mismas. Cada persona actúa bajo su propio criterio y libre voluntad.'
}, {
  heading: '11. Jurisdicción',
  body: 'Estos términos se rigen por las leyes de la República de Chile. Cualquier controversia será sometida a los tribunales competentes de la ciudad de Antofagasta.'
}, {
  heading: '12. Contacto',
  body: 'Para cualquier consulta sobre estos términos, puedes comunicarte directamente por WhatsApp.'
}];